import React from 'react';
import { Link } from 'react-router-dom';

import PageLayout from 'components/common/PageLayout';
import { ENTERPRISE_CONTACT_LINK, STUDIO_ROUTE } from 'components/constants';

import styles from './BrandGovernance.module.scss';

const responsibilities = [
  {
    number: '01',
    title: 'Designers own the system',
    text: 'Define the visual language, responsive behavior, components and reusable templates in Studio.',
  },
  {
    number: '02',
    title: 'Teams edit approved content',
    text: 'Expose the fields each team should update while brand-critical layout and behavior stay protected.',
  },
  {
    number: '03',
    title: 'Admins control access and publishing',
    text: 'Set roles, organize teams and products, and keep review and publishing rights with the right people.',
  },
];

const governanceControls = [
  {
    title: 'Approved templates',
    text: 'Turn a proven product into a governed starting point for a department, brand, market or recurring campaign.',
  },
  {
    title: 'Reusable component library',
    text: 'Give teams access to the same approved cards, interactions, controls and responsive patterns.',
  },
  {
    title: 'Protected editing',
    text: 'Lock layout, styling and behavior while keeping selected text, media and data fields editable.',
  },
  {
    title: 'Roles and permissions',
    text: 'Control who can create, edit, review, publish and access data across the organization.',
  },
  {
    title: 'Review and publishing controls',
    text: 'Keep draft work separate from live products and reserve approval and publishing for authorized roles.',
  },
  {
    title: 'Central product management',
    text: 'Manage templates, active products, ownership and access from one organization-level workspace.',
  },
];

const workflow = [
  {
    title: 'Define',
    text: 'Designers build the visual system, components and responsive behavior.',
  },
  {
    title: 'Package',
    text: 'Save approved structures as templates and choose which fields remain editable.',
  },
  {
    title: 'Assign',
    text: 'Give each team the right template, product access and publishing role.',
  },
  {
    title: 'Create',
    text: 'Teams adapt content for a specific campaign, audience or market inside the approved boundaries.',
  },
  {
    title: 'Review and publish',
    text: 'The right owner reviews the product and controls when it goes live.',
  },
];

const scaleContexts = [
  {
    title: 'Across teams',
    text: 'Marketing, product, digital, HR and data teams work from the same governed foundation.',
  },
  {
    title: 'Across brands',
    text: 'Maintain a distinct system, template library and access model for each brand or business unit.',
  },
  {
    title: 'Across markets',
    text: 'Reuse an approved structure while local teams adapt the content and variations they are allowed to change.',
  },
  {
    title: 'Across products',
    text: 'Keep active experiences, ownership and publishing status visible in one product library.',
  },
];

const BrandGovernance: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Brand governance</p>
            <h1>Scale creation without losing the brand system.</h1>
            <p className={styles.heroText}>
              Designers define templates, components, responsive behavior and editing boundaries in Studio. Other teams
              update approved content, follow the right review path and publish without rebuilding the design.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
              <Link to={STUDIO_ROUTE} className={styles.textButton}>
                Explore Studio
              </Link>
            </div>
          </div>

          <aside className={styles.heroPrinciples} aria-label="Brand governance operating principles">
            <p>One system, clear responsibilities</p>
            {responsibilities.map((item) => (
              <div key={item.title}>
                <span>{item.number}</span>
                <strong>{item.title}</strong>
                <small>{item.text}</small>
              </div>
            ))}
          </aside>
        </div>

        <figure className={styles.governanceWorkspace}>
          <figcaption className={styles.srOnly}>
            A Storycards template with a shared component library, protected layout and editable content fields.
          </figcaption>
          <div className={styles.workspaceBar}>
            <div className={styles.windowControls} aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
            <span>Spring campaign / Product finder</span>
            <em>
              <i /> Approved template
            </em>
          </div>
          <div className={styles.workspaceBody}>
            <div className={styles.libraryPanel}>
              <span className={styles.panelHeading}>Brand system</span>
              <div className={styles.brandIdentity}>
                <i>SC</i>
                <div>
                  <strong>Storycards</strong>
                  <small>Enterprise system</small>
                </div>
              </div>
              <span className={styles.panelLabel}>Approved components</span>
              <ul>
                <li>
                  <i className={styles.componentIcon} /> Product header <b>12</b>
                </li>
                <li>
                  <i className={styles.componentIcon} /> Answer cards <b>08</b>
                </li>
                <li>
                  <i className={styles.componentIcon} /> Form fields <b>14</b>
                </li>
                <li>
                  <i className={styles.componentIcon} /> Action buttons <b>06</b>
                </li>
              </ul>
              <span className={styles.libraryStatus}>
                <i /> System synced
              </span>
            </div>

            <div className={styles.canvasArea}>
              <div className={styles.canvasToolbar}>
                <span>Desktop</span>
                <span>1440 × 900</span>
                <div>
                  <i /> 100%
                </div>
              </div>
              <article className={styles.productCanvas}>
                <span className={styles.productEyebrow}>Product finder</span>
                <h2>Find the right plan for your team.</h2>
                <p>Answer three questions and compare the options that fit your organization.</p>
                <div className={styles.answerGrid}>
                  <span>1 to 20 people</span>
                  <span>21 to 100 people</span>
                  <span>More than 100</span>
                </div>
                <span className={styles.canvasButton}>Continue</span>
                <span className={`${styles.layerBadge} ${styles.lockedBadge}`}>Layout protected</span>
                <span className={`${styles.layerBadge} ${styles.editableBadge}`}>Copy editable</span>
              </article>
            </div>

            <div className={styles.rulesPanel}>
              <span className={styles.panelHeading}>Editing rules</span>
              <div className={styles.ruleSummary}>
                <strong>Content team</strong>
                <small>Campaign editors</small>
              </div>
              <div className={styles.ruleList}>
                <div>
                  <span>Text and labels</span>
                  <b className={styles.on}>Editable</b>
                </div>
                <div>
                  <span>Images</span>
                  <b className={styles.on}>Editable</b>
                </div>
                <div>
                  <span>Brand styles</span>
                  <b>Locked</b>
                </div>
                <div>
                  <span>Responsive layout</span>
                  <b>Locked</b>
                </div>
                <div>
                  <span>Interactions</span>
                  <b>Locked</b>
                </div>
              </div>
              <div className={styles.publishRule}>
                <span>Publishing</span>
                <strong>Approval required</strong>
                <small>Brand manager or admin</small>
              </div>
            </div>
          </div>
        </figure>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>Governance starts in the design system, not at the end of review.</h2>
          <div>
            <p>
              Storycards separates system design from day-to-day content work. Designers decide what is reusable, what
              stays protected and which fields another team can change.
            </p>
            <p>
              Everyone works in the same product. Content teams do not need a new design handoff for every update, and
              designers do not need to inspect the same brand decisions again and again.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.responsibilitySection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Clear ownership</p>
              <h2>Give each team the control its job requires.</h2>
            </div>
            <p>
              Governance works when the design team can protect the system and operating teams can still complete their
              work without a queue of manual design requests.
            </p>
          </header>
          <div className={styles.responsibilityGrid}>
            {responsibilities.map((item) => (
              <article key={item.title}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.controlsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Governance controls</p>
              <h2>Control the parts that should stay consistent.</h2>
            </div>
            <p>
              Build the rules into the workspace so teams see what they can use, what they can edit and who can approve
              the finished product.
            </p>
          </header>
          <div className={styles.controlsGrid}>
            {governanceControls.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.workflowSection}>
        <div className={styles.sectionInner}>
          <header className={styles.workflowHeader}>
            <p className={styles.eyebrow}>From system to live product</p>
            <h2>A governance model that keeps work moving.</h2>
            <p>Set the system once, then let each team move through a clear creation and publishing path.</p>
          </header>
          <ol className={styles.workflowList}>
            {workflow.map((item, index) => (
              <li key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className={styles.scaleSection}>
        <div className={styles.sectionInner}>
          <header className={styles.scaleHeader}>
            <p className={styles.eyebrow}>Organization-wide consistency</p>
            <h2>One operating model across teams, brands and markets.</h2>
          </header>
          <div className={styles.scaleGrid}>
            {scaleContexts.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Build your brand system into the way teams create.</h2>
          <div>
            <p>Plan the templates, permissions and publishing model your organization needs.</p>
            <div className={styles.ctaActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
              <Link to={STUDIO_ROUTE} className={styles.textButton}>
                Explore Studio
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default BrandGovernance;
